import { useState, createContext, useContext } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  CheckCircle,
  AlertCircle,
  XCircle,
  Info,
  X,
  Zap,
  TrendingUp,
  Target,
  Activity,
} from "lucide-react";

// Toast Context
const ToastContext = createContext();

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const addToast = (toast) => {
    const id = Date.now() + Math.random();
    const newToast = { id, ...toast };
    setToasts((prev) => [...prev, newToast]);

    // Auto remove after duration
    setTimeout(() => {
      removeToast(id);
    }, toast.duration || 5000);
  };

  const removeToast = (id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  };

  const success = (message, options = {}) => {
    addToast({
      type: "success",
      message,
      icon: CheckCircle,
      ...options,
    });
  };

  const error = (message, options = {}) => {
    addToast({
      type: "error",
      message,
      icon: XCircle,
      ...options,
    });
  };

  const warning = (message, options = {}) => {
    addToast({
      type: "warning",
      message,
      icon: AlertCircle,
      ...options,
    });
  };

  const info = (message, options = {}) => {
    addToast({
      type: "info",
      message,
      icon: Info,
      ...options,
    });
  };

  // FTP Zone specific toasts
  const zoneToast = (zone, message, options = {}) => {
    const zoneIcons = {
      recovery: Activity,
      endurance: TrendingUp,
      tempo: Target,
      threshold: Zap,
      neuromuscular: Zap,
    };

    addToast({
      type: "zone",
      zone,
      message,
      icon: zoneIcons[zone] || Info,
      ...options,
    });
  };

  const value = {
    toasts,
    addToast,
    removeToast,
    success,
    error,
    warning,
    info,
    zoneToast,
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastContainer />
    </ToastContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
}

// Toast Container
function ToastContainer() {
  const { toasts, removeToast } = useToast();

  return (
    <div className="fixed top-4 right-4 z-50 space-y-2">
      <AnimatePresence>
        {toasts.map((toast) => (
          <Toast key={toast.id} toast={toast} onRemove={removeToast} />
        ))}
      </AnimatePresence>
    </div>
  );
}

// Individual Toast
function Toast({ toast, onRemove }) {
  const { type, message, icon: Icon, zone, duration = 5000 } = toast;

  const toastStyles = {
    success: {
      bg: "bg-success-50",
      border: "border-success-200",
      text: "text-success-800",
      icon: "text-success-500",
      progress: "bg-success-500",
    },
    error: {
      bg: "bg-danger-50",
      border: "border-danger-200",
      text: "text-danger-800",
      icon: "text-danger-500",
      progress: "bg-danger-500",
    },
    warning: {
      bg: "bg-warning-50",
      border: "border-warning-200",
      text: "text-warning-800",
      icon: "text-warning-500",
      progress: "bg-warning-500",
    },
    info: {
      bg: "bg-primary-50",
      border: "border-primary-200",
      text: "text-primary-800",
      icon: "text-primary-500",
      progress: "bg-primary-500",
    },
    zone: {
      bg: `bg-zones-${zone}-50`,
      border: `border-zones-${zone}-200`,
      text: `text-zones-${zone}-800`,
      icon: `text-zones-${zone}-500`,
      progress: `bg-zones-${zone}-500`,
    },
  };

  const styles = toastStyles[type] || toastStyles.info;

  return (
    <motion.div
      initial={{ opacity: 0, x: 300, scale: 0.8 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, x: 300, scale: 0.8 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
      className={`max-w-sm w-full ${styles.bg} ${styles.border} border rounded-xl shadow-modern-lg overflow-hidden`}
    >
      {/* Progress Bar */}
      <motion.div
        className={`h-1 ${styles.progress}`}
        initial={{ width: "100%" }}
        animate={{ width: "0%" }}
        transition={{ duration: duration / 1000, ease: "linear" }}
      />

      {/* Toast Content */}
      <div className="p-4">
        <div className="flex items-start space-x-3">
          <div className={`flex-shrink-0 ${styles.icon}`}>
            <Icon className="w-5 h-5" />
          </div>

          <div className="flex-1 min-w-0">
            <p className={`text-sm font-medium ${styles.text}`}>{message}</p>
            {zone && (
              <p className="text-xs text-gray-500 mt-1 capitalize">
                Zone: {zone}
              </p>
            )}
          </div>

          <button
            onClick={() => onRemove(toast.id)}
            className={`flex-shrink-0 ${styles.icon} hover:opacity-70 transition-opacity`}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </motion.div>
  );
}

// Toast Hook for easy usage
// eslint-disable-next-line react-refresh/only-export-components
export function useToastActions() {
  const toast = useToast();

  return {
    // Success notifications
    showSuccess: (message, options) => toast.success(message, options),

    // Error notifications
    showError: (message, options) => toast.error(message, options),

    // Warning notifications
    showWarning: (message, options) => toast.warning(message, options),

    // Info notifications
    showInfo: (message, options) => toast.info(message, options),

    // FTP Zone notifications
    showZoneToast: (zone, message, options) =>
      toast.zoneToast(zone, message, options),

    // Training specific notifications
    showWorkoutComplete: (workoutName, duration) =>
      toast.success(`Workout "${workoutName}" completed in ${duration}`, {
        icon: Activity,
      }),

    showFTPUpdate: (oldFTP, newFTP) => {
      const change = newFTP - oldFTP;
      const changeText = change > 0 ? `+${change}W` : `${change}W`;
      toast.success(`FTP updated: ${oldFTP}W → ${newFTP}W (${changeText})`, {
        icon: TrendingUp,
        duration: 6000,
      });
    },

    showPlanGenerated: (planType, duration) =>
      toast.success(`${planType} training plan generated for ${duration}`, {
        icon: Target,
        duration: 5000,
      }),

    showZoneAchievement: (zone, message) =>
      toast.zoneToast(zone, `Zone Achievement: ${message}`, {
        duration: 7000,
      }),

    showNutritionPlan: (planType) =>
      toast.info(`${planType} nutrition plan created`, {
        icon: Activity,
        duration: 4000,
      }),
  };
}

// Quick Toast Components
export function QuickToast({ type, message, onClose }) {
  const toastStyles = {
    success: "bg-success-50 border-success-200 text-success-800",
    error: "bg-danger-50 border-danger-200 text-danger-800",
    warning: "bg-warning-50 border-warning-200 text-warning-800",
    info: "bg-primary-50 border-primary-200 text-primary-800",
  };

  const icons = {
    success: CheckCircle,
    error: XCircle,
    warning: AlertCircle,
    info: Info,
  };

  const Icon = icons[type] || Info;

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className={`fixed top-4 left-1/2 transform -translate-x-1/2 z-50 p-4 rounded-xl border ${toastStyles[type]} shadow-modern-lg`}
    >
      <div className="flex items-center space-x-2">
        <Icon className="w-5 h-5" />
        <span className="font-medium">{message}</span>
        {onClose && (
          <button
            onClick={onClose}
            className="ml-2 opacity-70 hover:opacity-100"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    </motion.div>
  );
}
